/**
 * C3：Page Visibility 工具 — 仅前台 Tab 用正常轮询间隔，后台降频或暂停。
 */

export function isDocumentVisible() {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

/**
 * @param {(visible: boolean) => void} listener
 * @returns {() => void} unsubscribe
 */
export function subscribePageVisibility(listener) {
  if (typeof document === 'undefined') return () => {};
  const onChange = () => {
    listener(isDocumentVisible());
  };
  document.addEventListener('visibilitychange', onChange);
  return () => document.removeEventListener('visibilitychange', onChange);
}

/**
 * 解析后台间隔：`pause` / false / 0 → 暂停；未传 → max(visible*3, 12s)。
 * @param {number} visibleMs
 * @param {number|'pause'|false|null|undefined} backgroundMs
 * @returns {number|null} null 表示暂停
 */
export function resolveBackgroundPollIntervalMs(visibleMs, backgroundMs) {
  if (backgroundMs === 'pause' || backgroundMs === false || backgroundMs === 0) {
    return null;
  }
  if (typeof backgroundMs === 'number' && Number.isFinite(backgroundMs) && backgroundMs > 0) {
    return Math.floor(backgroundMs);
  }
  const base = Math.max(500, Number(visibleMs) || 3000);
  return Math.max(base * 3, 12_000);
}

/**
 * 异步轮询间隔：前台用 visibleMs；后台降频（默认 max(3×, 12s)）；
 * 切回前台立即结束等待以便马上 tick；backgroundMs=pause 时后台挂起直至可见。
 * @param {number} visibleMs
 * @param {number|'pause'|false|null|undefined} [backgroundMs]
 * @returns {Promise<void>}
 */
export function sleepVisibilityAware(visibleMs, backgroundMs) {
  const targetVisible = Math.max(0, Number(visibleMs) || 0);
  if (targetVisible <= 0) return Promise.resolve();

  return new Promise((resolve) => {
    let done = false;
    let timerId = null;
    let unsub = null;

    const finish = () => {
      if (done) return;
      done = true;
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      if (unsub) {
        unsub();
        unsub = null;
      }
      resolve();
    };

    const schedule = () => {
      if (done) return;
      if (timerId != null) {
        clearTimeout(timerId);
        timerId = null;
      }
      const visible = isDocumentVisible();
      const hiddenMs = resolveBackgroundPollIntervalMs(targetVisible, backgroundMs);
      if (!visible && hiddenMs == null) {
        return;
      }
      const delay = visible ? targetVisible : hiddenMs;
      timerId = setTimeout(finish, delay);
    };

    unsub = subscribePageVisibility((visible) => {
      if (done) return;
      if (visible) {
        finish();
        return;
      }
      schedule();
    });

    schedule();
  });
}
